<!-- ManagedAccountOnboarding.vue -->
<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useUserStore } from "@/stores/useUserStore";
import { usePlanStore } from "@/stores/usePlanStore";
import * as managedAccountService from "@/services/managedAccountService";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";
import PerfilCombobox from "./PerfilCombobox.vue";
import TurmaCombobox from "./TurmaCombobox.vue";
import MemberSheetImport from "./MemberSheetImport.vue";
import { normalizeGroupName } from "@/utils/memberSheetImport";
import {
  DEFAULT_RELATIONSHIP_TYPE,
  PRIVACY_VERSION,
  TERMS_VERSION,
  calculateAgeFromIso,
  capitalizeFirst,
  getTodayLocalDateString,
  isMinor,
  isPasswordValid as isPasswordStrong,
  passwordCriteria,
  resolveConsentText,
  validateBirthDate,
  validateEmail,
} from "@/utils/memberValidation";

const emit = defineEmits(["done"]);

// 🔹 NOVO: arrastar o painel pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const onboardingStore = useOnboardingStore();
const subscriptionStore = useSubscriptionStore();
const userStore = useUserStore();
const planStore = usePlanStore();

// As regras de campo, os textos de consentimento e o perfil padrão vivem em
// utils/memberValidation.js: a importação de planilha aplica exatamente as
// mesmas, e quando elas eram duplicadas os dois caminhos divergiam.

// 'new' | 'existing' | 'import'
const mode = ref("new");
const isLoading = ref(false);
const errorMsg = ref("");
const successMsg = ref("");
const showPassword = ref(false);
const maxManaged = ref(0);
const activeMembersCount = ref(0);

// Cota de "pessoas em grupos" do plano (MaxGroupUser). Cada membro com turma
// consome uma vaga no envio do lote, que é tudo-ou-nada — por isso a conta
// é feita aqui também, antes de deixar a lista crescer além do que cabe.
const maxGroupUser = ref(0);
const groupUserCount = ref(0);

const localList = ref([]);
const batchSent = ref(false);
const editingIndex = ref(null);

const newUser = ref({
  userName: "",
  fullName: "",
  email: "",
  password: "",
  birthDate: "",
  relationshipType: "",
  groupName: "",
});

const searchQuery = ref("");
const searchResults = ref([]);
const isSearching = ref(false);
const searchError = ref("");
const selectedExisting = ref(null);
const searchPerformed = ref(false);
const relationshipTypeExisting = ref("");
const groupNameExisting = ref("");
let debounceTimer = null;

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveUserPhotoPath(user) {
  if (!user) return null;

  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.userPhotoUrl ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

function resolveLocalItemPhotoPath(item) {
  if (!item) return null;

  return (
    item.payload?.photoPath ||
    item.payload?.photoUrl ||
    item.payload?.userPhotoPath ||
    item.payload?.userPhotoUrl ||
    item.photoPath ||
    item.photoUrl ||
    null
  );
}

// Círculo de escola: a conta tem nome de instituição (cadastro "Sou uma
// escola", gravado em users.institution_name) ou o plano do titular do
// contexto é "Edu …" (o critério de HomePage.vue e do catálogo, que cobre as
// contas anteriores ao campo). Muda só o vocabulário dos campos: na escola o
// grupo é uma turma e o perfil é aluno/professor; numa família, não.
const isSchoolCircle = computed(
  () =>
    !!userStore.currentUser?.institutionName ||
    /^edu\b/i.test((userStore.currentUser?.planName || "").trim())
);
const groupFieldLabel = computed(() =>
  isSchoolCircle.value ? "Grupo/Turma" : "Grupo"
);
const groupFieldPlaceholder = computed(() =>
  isSchoolCircle.value ? "Ex: 9º A, Turma B…" : "Ex: Meus filhos, Turma B…"
);
const profileFieldPlaceholder = "Ex: aluno, professor, administrador…";

const registrationDate = ref(getTodayLocalDateString());

const birthDateError = computed(() =>
  validateBirthDate(newUser.value.birthDate, registrationDate.value)
);

const isMinorBirthDate = computed(() =>
  isMinor(newUser.value.birthDate, registrationDate.value)
);

const consentText = computed(() => {
  if (mode.value === "new") {
    return resolveConsentText(newUser.value.birthDate, registrationDate.value);
  }
  if (mode.value === "existing" && selectedExisting.value) {
    return resolveConsentText(
      selectedExisting.value.birthDate?.split("T")[0],
      registrationDate.value
    );
  }
  return resolveConsentText(null, registrationDate.value);
});

const shouldShowConsentBadge = computed(() => {
  if (mode.value === "new") {
    return newUser.value.birthDate !== "" && birthDateError.value === "";
  }
  if (mode.value === "existing") {
    return !!selectedExisting.value;
  }
  return false;
});

const localCount = computed(() => localList.value.length);

// Só conta nova ocupa vaga no envio. Conta existente vira convite, e convite
// pendente não conta no limite do plano: a vaga é checada quando a pessoa
// aceita.
const localNewCount = computed(
  () => localList.value.filter((i) => i.type === "new").length
);

const remainingSlots = computed(() =>
  Math.max(0, maxManaged.value - activeMembersCount.value)
);

const plannedMembersCount = computed(
  () => activeMembersCount.value + localNewCount.value
);

const remainingSlotsAfterLocal = computed(() =>
  Math.max(0, remainingSlots.value - localNewCount.value)
);

// Vagas de "pessoas em grupos" já descontando as contas novas da lista que
// têm turma. O convidado entra na turma só no aceite, e a cota é cobrada lá.
const stagedGroupLinks = computed(
  () =>
    localList.value.filter((i) => i.type === "new" && i.payload?.groupName)
      .length
);
const remainingGroupSlots = computed(() =>
  Math.max(0, maxGroupUser.value - groupUserCount.value - stagedGroupLinks.value)
);

const canAddMore = computed(() => {
  if (editingIndex.value !== null) return true;
  if (mode.value === "existing") return true;
  return localNewCount.value < remainingSlots.value;
});

const progressPct = computed(() =>
  maxManaged.value > 0
    ? Math.min(
        100,
        Math.round(
          ((activeMembersCount.value + localNewCount.value) / maxManaged.value) *
            100
        )
      )
    : 0
);

const errorCount = computed(
  () => localList.value.filter((i) => i.status === "error").length
);

const showRetryBadge = computed(() => batchSent.value && errorCount.value > 0);
const isEditing = computed(() => editingIndex.value !== null);
const editingItem = computed(() =>
  editingIndex.value !== null ? localList.value[editingIndex.value] : null
);

const passwordCriteriaMet = computed(() =>
  passwordCriteria(newUser.value.password)
);
const isPasswordValid = computed(() => isPasswordStrong(newUser.value.password));

const emailError = computed(() => validateEmail(newUser.value.email));

const isNewUserFormValid = computed(
  () =>
    newUser.value.userName.trim() !== "" &&
    newUser.value.email.trim() !== "" &&
    emailError.value === "" &&
    newUser.value.birthDate !== "" &&
    birthDateError.value === "" &&
    newUser.value.password.trim() !== "" &&
    isPasswordValid.value
);

function isResultMinor(user) {
  if (!user.birthDate) return false;
  const age = calculateAgeFromIso(user.birthDate);
  return age !== null && age < 18;
}

function isResultAlreadyMember(user) {
  return !!user.isAlreadyMember;
}

onMounted(async () => {
  try {
    // O limite de membros vem do consumo agregado: o endpoint já resolve o
    // titular do contexto, então o membro delegado (professor cadastrando
    // aluno) enxerga o limite do plano do titular. Buscar pela assinatura
    // própria zerava o número para ele, que não tem assinatura.
    try {
      const usage = await managedAccountService.fetchAggregatedUsage();
      maxManaged.value = usage?.data?.maxManagedAccounts ?? 0;
      maxGroupUser.value = usage?.data?.maxGroupUser ?? 0;
      groupUserCount.value = usage?.data?.totalGroupUserCount ?? 0;
    } catch (usageError) {
      console.warn(
        "[ManagedAccountOnboarding] Falha ao ler consumo agregado:",
        usageError
      );
    }

    // Fallback pela assinatura própria (titular sem consumo agregado disponível).
    const userId = userStore.currentUser?.userId;
    if (!maxManaged.value && userId) {
      await subscriptionStore.fetchSubscriptionsPaged(1, 100, userId);
      const active = subscriptionStore.activeSubscription;
      if (active?.planId) {
        await planStore.getPlanById(active.planId);
        maxManaged.value = planStore.selectedPlan?.maxManagedAccounts ?? 0;
      }
    }
    const membersResponse = await managedAccountService.fetchAll();
    activeMembersCount.value = (membersResponse.data ?? []).length;
  } catch (e) {
    console.error("[ManagedAccountOnboarding] Erro ao inicializar:", e);
  }
});

onUnmounted(() => clearTimeout(debounceTimer));

function resetNewUserForm() {
  newUser.value = {
    userName: "",
    fullName: "",
    email: "",
    password: "",
    birthDate: "",
    relationshipType: "",
    groupName: "",
  };
  showPassword.value = false;
}

function resetExistingSelection() {
  searchQuery.value = "";
  searchResults.value = [];
  searchError.value = "";
  selectedExisting.value = null;
  searchPerformed.value = false;
  relationshipTypeExisting.value = "";
  groupNameExisting.value = "";
  clearTimeout(debounceTimer);
}

// Turma do formulário manual: mesma régua da planilha (normalizeGroupName) e
// a mesma cota — uma vaga de "pessoas em grupos" por item com turma. Devolve
// a mensagem de erro ou "" quando pode seguir.
function validateGroupChoice(rawGroupName) {
  const groupName = normalizeGroupName(rawGroupName);
  if (!groupName) return "";
  if (groupName.toLowerCase() === "todos") {
    return '"Todos" é um nome de grupo reservado pelo sistema. Use outro nome de turma.';
  }
  // Ao editar um item que já tinha turma, a vaga dele já está descontada.
  const editingHadGroup =
    editingIndex.value !== null && !!editingItem.value?.payload?.groupName;
  if (!editingHadGroup && remainingGroupSlots.value <= 0) {
    return `Seu plano não tem mais vagas para pessoas em grupos (${maxGroupUser.value}). Deixe a turma em branco ou amplie o plano.`;
  }
  return "";
}

function clearEditingState() {
  editingIndex.value = null;
}

function clearTransientMessages() {
  errorMsg.value = "";
  successMsg.value = "";
}

function switchMode(m, options = {}) {
  const { preserveEditing = false } = options;
  mode.value = m;
  clearTransientMessages();
  if (!preserveEditing) {
    clearEditingState();
    resetNewUserForm();
    resetExistingSelection();
  } else if (m === "new") {
    resetExistingSelection();
  } else if (m === "existing") {
    resetNewUserForm();
  }
}

async function executeSearch() {
  const q = searchQuery.value.trim();
  searchError.value = "";
  searchResults.value = [];
  selectedExisting.value = null;
  searchPerformed.value = false;

  if (!q || q.length < 2) return;

  const alreadyIds = localList.value
    .filter((i, idx) => i.type === "existing" && idx !== editingIndex.value)
    .map((i) => i.payload.userId)
    .filter(Boolean);

  isSearching.value = true;
  try {
    const response = await managedAccountService.searchUsersForLink(
      q,
      alreadyIds
    );
    searchResults.value = response.data ?? [];
    searchPerformed.value = true;
    if (searchResults.value.length === 0) {
      searchError.value = `Nenhum usuário encontrado para "${q}".`;
    }
  } catch (e) {
    searchError.value = e.response?.data?.message || "Erro ao buscar usuário.";
  } finally {
    isSearching.value = false;
  }
}

function handleSearchInput() {
  clearTimeout(debounceTimer);
  const q = searchQuery.value.trim();
  if (!q || q.length < 2) {
    searchResults.value = [];
    searchError.value = "";
    searchPerformed.value = false;
    return;
  }
  debounceTimer = setTimeout(executeSearch, 350);
}

function handleSearch() {
  clearTimeout(debounceTimer);
  executeSearch();
}

function selectExistingUser(u) {
  if (isResultAlreadyMember(u)) return;
  selectedExisting.value = u;
}

function finalizeLocalItemMutation(message) {
  successMsg.value = message;
  clearEditingState();
  batchSent.value = localList.value.some((i) => i.status === "error");
  setTimeout(() => {
    successMsg.value = "";
  }, 3000);
}

function normalizeListItem(item) {
  return {
    type: item.type,
    label: item.label,
    email: item.email,
    status: item.status ?? "pending",
    errorReason: item.errorReason ?? null,
    payload: { ...item.payload },
  };
}

function updateLocalItemAt(index, item) {
  localList.value.splice(index, 1, normalizeListItem(item));
}

function addLocalItem(item) {
  localList.value.push(normalizeListItem(item));
}

function buildNewUserItem() {
  // capitalizeFirst: "filho" → "Filho", "FILHO" → "FILHO" mantém caps restantes
  // relationshipType pode vir null do combobox quando a seleção é limpa.
  const rawType = (newUser.value.relationshipType || "").trim();
  const normalizedType = capitalizeFirst(rawType) || DEFAULT_RELATIONSHIP_TYPE;

  return {
    type: "new",
    label: newUser.value.fullName.trim() || newUser.value.userName.trim(),
    email: newUser.value.email.trim(),
    status: "pending",
    errorReason: null,
    payload: {
      userName: newUser.value.userName.trim(),
      fullName: newUser.value.fullName.trim(),
      email: newUser.value.email.trim(),
      password: newUser.value.password,
      birthDate: newUser.value.birthDate || null,
      relationshipType: normalizedType,
      groupName: normalizeGroupName(newUser.value.groupName) || null,
      consentTextSnapshot: consentText.value,
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION,
    },
  };
}

function handleAddNew() {
  errorMsg.value = "";
  if (!isNewUserFormValid.value) return;

  const groupError = validateGroupChoice(newUser.value.groupName);
  if (groupError) {
    errorMsg.value = groupError;
    return;
  }

  const normalizedEmail = newUser.value.email.trim().toLowerCase();
  const duplicateEmail = localList.value.some(
    (i, idx) =>
      idx !== editingIndex.value &&
      (i.payload.email || "").trim().toLowerCase() === normalizedEmail
  );

  if (duplicateEmail) {
    errorMsg.value = "Este e-mail já foi adicionado à lista.";
    return;
  }

  const normalizedUserName = newUser.value.userName.trim().toLowerCase();
  const duplicateUserName = localList.value.some(
    (i, idx) =>
      idx !== editingIndex.value &&
      i.type === "new" &&
      (i.payload.userName || "").trim().toLowerCase() === normalizedUserName
  );

  if (duplicateUserName) {
    errorMsg.value = "Este nome de usuário já foi adicionado à lista.";
    return;
  }

  const item = buildNewUserItem();
  const itemLabel = item.label;

  if (editingIndex.value !== null) {
    updateLocalItemAt(editingIndex.value, item);
    resetNewUserForm();
    finalizeLocalItemMutation(`"${itemLabel}" atualizado(a) na lista.`);
    return;
  }

  addLocalItem(item);
  successMsg.value = `"${itemLabel}" adicionado à lista.`;
  resetNewUserForm();
  batchSent.value = false;
  setTimeout(() => {
    successMsg.value = "";
  }, 3000);
}

function buildExistingItem() {
  // capitalizeFirst: "filho" → "Filho", "FILHO" → "FILHO" mantém caps restantes
  // relationshipTypeExisting pode vir null do combobox quando a seleção é limpa.
  const rawType = (relationshipTypeExisting.value || "").trim();
  const normalizedType = capitalizeFirst(rawType) || DEFAULT_RELATIONSHIP_TYPE;

  return {
    type: "existing",
    label: selectedExisting.value.fullName || selectedExisting.value.userName,
    email: selectedExisting.value.email,
    status: "pending",
    errorReason: null,
    payload: {
      userId: selectedExisting.value.userId,
      userName: selectedExisting.value.userName ?? "",
      fullName: selectedExisting.value.fullName ?? "",
      email: selectedExisting.value.email ?? "",
      photoPath: selectedExisting.value.photoPath ?? null,
      relationshipType: normalizedType,
      groupName: normalizeGroupName(groupNameExisting.value) || null,
      consentTextSnapshot: consentText.value,
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION,
    },
  };
}

function handleLinkExisting() {
  errorMsg.value = "";
  if (!selectedExisting.value) {
    errorMsg.value = "Selecione um usuário da lista de resultados.";
    return;
  }

  const groupError = validateGroupChoice(groupNameExisting.value);
  if (groupError) {
    errorMsg.value = groupError;
    return;
  }

  if (isResultAlreadyMember(selectedExisting.value)) {
    errorMsg.value = "Este usuário já é membro do seu círculo.";
    return;
  }

  const duplicateExisting = localList.value.some(
    (i, idx) =>
      idx !== editingIndex.value &&
      i.payload.userId === selectedExisting.value.userId
  );

  if (duplicateExisting) {
    errorMsg.value = "Este usuário já foi adicionado à lista.";
    return;
  }

  const item = buildExistingItem();
  const itemLabel = item.label;

  if (editingIndex.value !== null) {
    updateLocalItemAt(editingIndex.value, item);
    resetExistingSelection();
    finalizeLocalItemMutation(`"${itemLabel}" atualizado(a) na lista.`);
    return;
  }

  addLocalItem(item);
  successMsg.value = `"${itemLabel}" adicionado à lista.`;
  resetExistingSelection();
  batchSent.value = false;
  setTimeout(() => {
    successMsg.value = "";
  }, 3000);
}

function removeFromList(index) {
  localList.value.splice(index, 1);

  if (editingIndex.value === index) {
    clearEditingState();
    resetNewUserForm();
    resetExistingSelection();
  } else if (editingIndex.value !== null && index < editingIndex.value) {
    editingIndex.value--;
  }

  if (localList.value.every((i) => i.status === "pending")) {
    batchSent.value = false;
  }
}

function handleErrorCardClick(item, index) {
  if (item.status !== "error") return;
  if (item.type === "existing") return;

  clearTransientMessages();
  editingIndex.value = index;

  switchMode("new", { preserveEditing: true });
  newUser.value = {
    userName: item.payload.userName ?? "",
    fullName: item.payload.fullName ?? "",
    email: item.payload.email ?? "",
    password: item.payload.password ?? "",
    birthDate: item.payload.birthDate ?? "",
    relationshipType: item.payload.relationshipType ?? "",
    groupName: item.payload.groupName ?? "",
  };
  showPassword.value = false;
}

async function handleDone() {
  if (localList.value.length === 0) {
    onboardingStore.reset();
    emit("done");
    return;
  }

  isLoading.value = true;
  errorMsg.value = "";

  localList.value = localList.value.map((item) => ({
    ...item,
    status: item.status === "error" ? "error" : "pending",
    payload: { ...item.payload },
  }));

  try {
    const items = localList.value.map((i) => ({ ...i.payload }));
    const response = await managedAccountService.processBatch(items);
    const result = response.data;

    const failedCount = result.failed?.length ?? 0;

    if (failedCount === 0) {
      onboardingStore.reset();
      emit("done", {
        skipped: false,
        created: result.succeeded?.length ?? 0,
        invited: result.invited?.length ?? 0,
      });
      return;
    }

    batchSent.value = true;

    const failedByIndex = new Map(
      result.failed.map((f) => [f.index, f.reason])
    );

    localList.value = localList.value.map((item, idx) => {
      if (failedByIndex.has(idx)) {
        return {
          ...item,
          status: "error",
          errorReason: failedByIndex.get(idx),
          payload: { ...item.payload },
        };
      }
      return {
        ...item,
        status: "pending",
        errorReason: null,
        payload: { ...item.payload },
      };
    });
  } catch (e) {
    errorMsg.value =
      e.response?.data?.message || "Erro ao enviar as contas. Tente novamente.";
  } finally {
    isLoading.value = false;
  }
}

function handleSkipNow() {
  onboardingStore.reset();
  emit("done", { skipped: true });
}

// ─── Importação de planilha ──────────────────────────────────────────────────

// A conferência é uma tabela de 8 colunas: com os 280px da "Lista para
// confirmar" ao lado ela fica espremida em ~540px. Enquanto se confere, o
// painel inteiro vira a tabela; ao adicionar, volta ao layout de duas colunas
// com a lista já preenchida.
const isImportExpanded = ref(false);
const isPanelWide = computed(
  () => mode.value === "import" && isImportExpanded.value
);

function handleImportAdd(items) {
  clearTransientMessages();
  items.forEach((item) => addLocalItem(item));
  batchSent.value = false;
  successMsg.value = `${items.length} conta(s) adicionada(s) à lista pela planilha. Revise ao lado e clique em Concluir.`;
  setTimeout(() => {
    successMsg.value = "";
  }, 6000);
}
</script>

<template>
  <div class="onboarding-backdrop">
    <div
      ref="panelRef"
      class="onboarding-panel"
      :class="{ 'onboarding-panel--wide': isPanelWide }"
      :style="panelStyle"
    >
      <div
        class="panel-header"
        :class="{
 'panel-header--draggable': isDraggable,
          'panel-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <span class="header-icon">📚</span>
        <div class="header-text">
          <h2 class="panel-title">Adicionar contas de membros</h2>
          <p class="panel-subtitle">
            Você já tem
            <strong>{{ plannedMembersCount }} de {{ maxManaged }}</strong>
            membro(s) considerando os já ativos e os adicionados à lista. Ainda
            pode adicionar
            <strong>{{ remainingSlotsAfterLocal }}</strong>
            nesta sessão.
          </p>
        </div>
        <button
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
          @click="handleSkipNow"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="panel-body" :class="{ 'panel-body--wide': isPanelWide }">
        <div class="col-form">
          <div class="mode-selector">
            <span class="mode-selector-label">Tipo de cadastro</span>

            <div
              class="mode-tabs"
              role="tablist"
              aria-label="Tipo de cadastro de membro"
            >
              <button
                type="button"
                role="tab"
                :aria-selected="mode === 'new'"
                :class="['tab-btn', mode === 'new' && 'tab-btn--active']"
                @click="switchMode('new')"
              >
                Nova conta
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="mode === 'existing'"
                :class="['tab-btn', mode === 'existing' && 'tab-btn--active']"
                @click="switchMode('existing')"
              >
                Convidar existente
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="mode === 'import'"
                :class="['tab-btn', mode === 'import' && 'tab-btn--active']"
                @click="switchMode('import')"
              >
                Importar planilha
              </button>
            </div>
          </div>

          <div class="form-area">
            <div v-if="!canAddMore" class="limit-reached">
              <strong>Limite atingido.</strong>
              <p class="limit-hint">
                Seu plano permite {{ maxManaged }} membro(s). Você já tem
                {{ activeMembersCount }} ativo(s)
                {{ localNewCount > 0 ? `e ${localNewCount} na lista.` : "." }}
                Gerencie seus membros no dashboard para liberar vagas.
              </p>
            </div>

            <template v-else-if="mode === 'new'">
              <div
                v-if="isEditing && editingItem?.type === 'new'"
                class="edit-badge"
              >
                Editando conta com erro da lista
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Nome completo</label>
                  <input
                    v-model="newUser.fullName"
                    type="text"
                    class="field-input"
                    placeholder="Nome completo"
                    autocomplete="off"
                  />
                </div>
                <div class="field-group">
                  <label class="field-label"
                    >Nome de usuário <span class="required">*</span></label
                  >
                  <input
                    v-model="newUser.userName"
                    type="text"
                    class="field-input"
                    placeholder="usuario123"
                    autocomplete="off"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label"
                    >E-mail <span class="required">*</span></label
                  >
                  <input
                    v-model="newUser.email"
                    type="email"
                    class="field-input"
                    :class="{ 'field-input--error': emailError }"
                    placeholder="email@exemplo.com"
                    autocomplete="off"
                  />
                  <p v-if="emailError" class="field-error">{{ emailError }}</p>
                </div>
                <div class="field-group">
                  <label class="field-label"
                    >Data de nascimento <span class="required">*</span></label
                  >
                  <input
                    v-model="newUser.birthDate"
                    type="date"
                    class="field-input"
                    :max="registrationDate"
                    autocomplete="off"
                  />
                  <p v-if="birthDateError" class="field-error">
                    {{ birthDateError }}
                  </p>
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label"
                    >Senha <span class="required">*</span></label
                  >
                  <div class="password-wrapper">
                    <input
                      v-model="newUser.password"
                      :type="showPassword ? 'text' : 'password'"
                      class="field-input"
                      placeholder="Mín. 8 chars, letra + número"
                      autocomplete="new-password"
                    />
                    <button
                      type="button"
                      class="password-toggle-btn"
                      @click="showPassword = !showPassword"
                      :aria-label="
                        showPassword ? 'Ocultar senha' : 'Exibir senha'
                      "
                    >
                      <svg
                        v-if="showPassword"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.938 17.938l-12-12"
                        />
                      </svg>
                    </button>
                  </div>
                  <p class="password-hints" aria-live="polite">
                    Use
                    <span :class="{ met: passwordCriteriaMet.length }"
                      >8+ caracteres</span
                    >
                    com
                    <span :class="{ met: passwordCriteriaMet.letter }"
                      >letras</span
                    >
                    e
                    <span :class="{ met: passwordCriteriaMet.number }"
                      >números</span
                    >.
                  </p>
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Perfil</label>
                  <PerfilCombobox
                    v-model="newUser.relationshipType"
                    :placeholder="profileFieldPlaceholder"
                  />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ groupFieldLabel }}</label>
                  <TurmaCombobox
                    v-model="newUser.groupName"
                    :placeholder="groupFieldPlaceholder"
                    :label="groupFieldLabel"
                  />
                </div>
              </div>

              <div
                v-if="shouldShowConsentBadge"
                class="consent-box"
                :class="
 isMinorBirthDate ? 'consent-box--minor' : 'consent-box--edu'
                "
              >
                <p class="consent-text">{{ consentText }}</p>
              </div>

              <transition name="fade">
                <div v-if="errorMsg" class="feedback feedback--error">
                  {{ errorMsg }}
                </div>
              </transition>
              <transition name="fade">
                <div v-if="successMsg" class="feedback feedback--success">
                  {{ successMsg }}
                </div>
              </transition>

            </template>

            <template v-else-if="mode === 'existing'">
              <div class="field-group">
                <label class="field-label"
                  >Buscar por nome de usuário ou e-mail</label
                >
                <div class="search-row">
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="field-input"
                    placeholder="Ex: joao123 ou joao@email.com"
                    @input="handleSearchInput"
                    @keydown.enter.prevent="handleSearch"
                    autocomplete="off"
                  />
                  <button
                    type="button"
                    class="btn-search"
                    :disabled="isSearching || searchQuery.trim().length < 2"
                    @click="handleSearch"
                    title="Buscar agora"
                  >
                    <svg
                      v-if="!isSearching"
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 0 5 11a6 6 0 0 0 12 0z"
                      />
                    </svg>
                    <span v-else class="spinner"></span>
                  </button>
                </div>
                <p class="field-hint">
                  Os resultados aparecem automaticamente ao digitar.
                </p>
                <p class="field-hint">
                  Quem já tem conta recebe um convite no sino e por e-mail, e
                  só entra no seu círculo depois de aceitar. O convite vale 7
                  dias e não ocupa vaga do plano enquanto espera resposta.
                </p>
              </div>

              <div class="search-results-wrap">
                <div
                  v-if="
                    searchPerformed &&
                    searchResults.length === 0 &&
                    !isSearching
                  "
                  class="search-notice"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"
                    />
                  </svg>
                  <span
                    >Nenhum usuário encontrado para "<strong>{{
                      searchQuery
                    }}</strong
                    >".</span
                  >
                </div>

                <ul v-if="searchResults.length > 0" class="results-list">
                  <li
                    v-for="u in searchResults"
                    :key="u.userId"
                    class="result-item"
                    :class="{
 'result-item--selected':
                        selectedExisting?.userId === u.userId,
                      'result-item--blocked': isResultAlreadyMember(u),
                    }"
                    @click="selectExistingUser(u)"
                  >
                    <div class="result-info">
                      <div class="result-name-row">
                        <span class="result-avatar">
                          <span
                            v-if="getAvatarUrl(resolveUserPhotoPath(u))"
                            class="result-avatar-img"
                            :style="{
                              backgroundImage: `url('${getAvatarUrl(
                                resolveUserPhotoPath(u)
                              )}')`,
                            }"
                          ></span>

                          <i v-else class="fas fa-user result-avatar-icon"></i>
                        </span>

                        <span class="result-name">
                          {{ u.fullName || u.userName }}
                        </span>
                      </div>

                      <span class="result-meta">
                        @{{ u.userName }} · {{ u.email }}

                        <span
                          v-if="isResultAlreadyMember(u)"
                          class="result-badge result-badge--member"
                        >
                          já membro
                        </span>

                        <span
                          v-else-if="isResultMinor(u)"
                          class="result-badge result-badge--minor"
                        >
                          👶 menor · {{ calculateAgeFromIso(u.birthDate) }} anos
                        </span>
                      </span>
                    </div>
                    <svg
                      v-if="selectedExisting?.userId === u.userId"
                      class="w-4 h-4 result-check"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </li>
                </ul>
              </div>

              <div
                v-if="shouldShowConsentBadge"
                class="consent-box"
                :class="
 selectedExisting && isResultMinor(selectedExisting)
                    ? 'consent-box--minor'
                    : 'consent-box--edu'
                "
              >
                <p class="consent-text">{{ consentText }}</p>
              </div>

              <transition name="fade">
                <div v-if="searchError" class="feedback feedback--error">
                  {{ searchError }}
                </div>
              </transition>
              <transition name="fade">
                <div v-if="errorMsg" class="feedback feedback--error">
                  {{ errorMsg }}
                </div>
              </transition>
              <transition name="fade">
                <div v-if="successMsg" class="feedback feedback--success">
                  {{ successMsg }}
                </div>
              </transition>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Perfil</label>
                  <PerfilCombobox
                    v-model="relationshipTypeExisting"
                    :placeholder="profileFieldPlaceholder"
                  />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ groupFieldLabel }}</label>
                  <TurmaCombobox
                    v-model="groupNameExisting"
                    :placeholder="groupFieldPlaceholder"
                    :label="groupFieldLabel"
                  />
                </div>
              </div>

            </template>

            <!-- v-show, e não v-if: trocar de aba no meio da conferência não
                 pode jogar fora a planilha já lida e as correções feitas. -->
            <div
              v-show="canAddMore && mode === 'import'"
              class="import-slot"
            >
              <transition name="fade">
                <div v-if="successMsg" class="feedback feedback--success">
                  {{ successMsg }}
                </div>
              </transition>

              <MemberSheetImport
                :staged-items="localList"
                :remaining-slots="remainingSlotsAfterLocal"
                :remaining-group-slots="remainingGroupSlots"
                @add="handleImportAdd"
                @expand="isImportExpanded = $event"
              />
            </div>
          </div>

          <div v-if="mode !== 'import'" class="form-footer">
            <button
              v-if="canAddMore"
              type="button"
              class="btn-add"
              :disabled="
                isLoading ||
                (mode === 'new'
                  ? !isNewUserFormValid
                  : !selectedExisting || isResultAlreadyMember(selectedExisting))
              "
              @click="mode === 'new' ? handleAddNew() : handleLinkExisting()"
            >
              <svg
                class="btn-add-icon"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M12 5v14M5 12h14"
                />
              </svg>
              <span class="btn-label">{{ isEditing ? "Atualizar item" : "Adicionar à lista" }}</span>
            </button>
          </div>
        </div>

        <div v-show="!isPanelWide" class="col-members">
          <div class="members-header">
            <span class="members-title">Lista para confirmar</span>
            <span class="members-counter"
              >{{ localNewCount }} / {{ remainingSlots }}</span
            >
          </div>

          <div class="progress-bar-wrap">
            <div
              class="progress-bar-fill"
              :style="{ width: `${progressPct}%` }"
            ></div>
          </div>

          <div v-if="batchSent && errorCount > 0" class="batch-warning">
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v4m0 4h.01M12 3l9.196 16H2.804L12 3z"
              />
            </svg>
            <span>
              Nenhuma conta foi salva. Corrija os erros (em vermelho) ou
              remova-os e tente novamente.
            </span>
          </div>

          <div class="members-list-wrap">
            <p v-if="localCount === 0" class="members-empty">
              Adicione contas ao lado para confirmá-las aqui.
            </p>

            <ul v-if="localCount > 0" class="members-list">
              <li
                v-for="(item, idx) in localList"
                :key="idx"
                class="member-item"
                :class="[
 item.status === 'error'
                    ? 'member-item--error'
                    : 'member-item--ok',
                  item.status === 'error' &&
                    item.type === 'new' &&
                    'member-item--clickable',
                  editingIndex === idx && 'member-item--editing',
                ]"
                @click="handleErrorCardClick(item, idx)"
              >
                <div class="member-item-top">
                  <span
                    class="member-avatar"
                    :class="
 item.status === 'error' ? 'member-avatar--error' : ''
                    "
                  >
                    <span
                      v-if="getAvatarUrl(resolveLocalItemPhotoPath(item))"
                      class="member-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveLocalItemPhotoPath(item)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user member-avatar-icon"></i>
                  </span>
                  <div class="member-info">
                    <span class="member-name">{{ item.label }}</span>
                    <span class="member-email">{{ item.email }}</span>
                  </div>
                  <button
                    type="button"
                    class="btn-remove-item"
                    @click.stop="removeFromList(idx)"
                    title="Remover"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div class="member-relationship-row">
                  <span
                    v-if="item.payload?.groupName"
                    class="member-relationship-type member-group-name"
                    :title="`Turma: ${item.payload.groupName}`"
                    >{{ item.payload.groupName }}</span
                  >
                  <span class="member-relationship-type">{{
                    item.payload?.relationshipType || DEFAULT_RELATIONSHIP_TYPE
                  }}</span>
                </div>

                <div v-if="item.status === 'error'" class="member-error-detail">
                  <div class="error-edit-hint">
                    {{
                      item.type === "new"
                        ? "Clique para revisar este item."
                        : "Remova e tente novamente."
                    }}
                  </div>

                  <template v-if="item.type === 'new'">
                    <div class="error-field">
                      <span class="error-field-label">Usuário:</span>
                      <span class="error-field-value">{{
                        item.payload.userName
                      }}</span>
                    </div>
                    <div class="error-field">
                      <span class="error-field-label">Nascimento:</span>
                      <span class="error-field-value">{{
                        item.payload.birthDate ?? "—"
                      }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="error-field">
                      <span class="error-field-label">ID do usuário:</span>
                      <span class="error-field-value">{{
                        item.payload.userId
                      }}</span>
                    </div>
                  </template>

                  <div class="error-reason">
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    {{ item.errorReason }}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div class="members-footer">
            <transition name="fade">
              <div
                v-if="errorMsg && localCount > 0"
                class="feedback feedback--error mb-2"
              >
                {{ errorMsg }}
              </div>
            </transition>

            <div v-if="showRetryBadge" class="retry-badge">
              Revise e tente novamente
            </div>

            <div class="footer-actions">
              <button
                type="button"
                class="btn-skip-fixed"
                :disabled="isLoading"
                @click="handleSkipNow"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="btn-conclude"
                :disabled="isLoading || localCount === 0"
                @click="handleDone"
              >
                <i
                  v-if="isLoading"
                  class="btn-icon fas fa-spinner fa-spin"
                  aria-hidden="true"
                ></i>
                <i v-else class="btn-icon fas fa-check" aria-hidden="true"></i>
                <span class="btn-label">{{ isLoading ? "Enviando..." : "Concluir" }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.onboarding-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  background-color: var(--backdrop);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 36px 2rem 1.5rem;
  overflow-y: auto;
}
.onboarding-panel {
  background: var(--surface);
  border-radius: 16px;
  width: 100%;
  max-width: 860px;
  height: 710px;
  max-height: calc(100vh - 36px - 1.5rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-modal);
  position: relative;
}
.panel-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  flex-shrink: 0;
}
/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.panel-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.panel-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.panel-header--draggable .modal-close-btn {
  cursor: pointer;
}

.header-icon {
  font-size: 1.4rem;
  line-height: 1;
  flex-shrink: 0;
}
.header-text {
  flex: 1;
  min-width: 0;
}
.panel-title {
  font-size: 1.02rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.1rem;
}
.panel-subtitle {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin: 0;
}

.modal-close-btn {
  flex-shrink: 0;
}

.panel-body {
  display: grid;
  grid-template-columns: 1fr 280px;
  flex: 1;
  min-height: 0;
}

/* Conferência da planilha: o painel inteiro vira a tabela — ver isPanelWide. */
.onboarding-panel--wide {
  max-width: 1120px;
  transition: max-width 0.2s ease;
}
.panel-body--wide {
  grid-template-columns: 1fr;
}
.panel-body--wide .col-form {
  border-right: none;
}
.import-slot {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.col-form {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-right: 1px solid var(--neutral-200);
  overflow: hidden;
  background: var(--bg-content);
}
.mode-selector {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex-shrink: 0;
}

.mode-selector-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-light);
}

.mode-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.15rem;
  flex-shrink: 0;
  width: 100%;
  max-width: 480px;
  padding: 0.15rem;
  border: 1px solid var(--neutral-200);
  border-radius: 999px;
  background-color: var(--surface-3);
}

.tab-btn {
  height: 28px;
  padding: 0 0.65rem;
  border-radius: 999px;
  border: 1px solid transparent;
  background-color: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  white-space: nowrap;
}

.tab-btn:hover:not(:disabled) {
  background-color: var(--neutral-200);
}

.tab-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.tab-btn:focus,
.tab-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.tab-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tab-btn--active {
  background-color: #d8d0ff;
  border-color: #d8d0ff;
  color: var(--text-on-brand);
}

.tab-btn--active:hover:not(:disabled) {
  background-color: #d8d0ff;
}
.form-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  overflow: hidden;
  min-height: 0;
}

.form-footer {
  flex-shrink: 0;
  padding-top: 0.5rem;
  /* border-top: 1px solid var(--neutral-200); */
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  flex-shrink: 0;
}
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex-shrink: 0;
}
.field-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-dark);
}
.required {
  color: var(--danger);
}
.field-input {
  height: 32px;
  padding: 0 0.7rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--text-dark);
  background: var(--surface);
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.field-input:focus {
  outline: none;
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-brand);
}

.field-hint {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin: 0;
}
.field-error {
  font-size: 0.72rem;
  color: var(--danger-text);
  margin: 0.15rem 0 0;
}

.field-input--error,
.field-input--error:focus {
  border-color: var(--danger-text);
}
.password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.password-wrapper .field-input {
  padding-right: 2.5rem;
}
.password-toggle-btn {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-subtle);
  padding: 0.15rem;
  line-height: 1;
  transition: color 0.2s;
}
.password-toggle-btn:hover {
  color: var(--brand-light);
}
.password-hints {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin: 0;
}
.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}
.edit-badge {
  align-self: flex-start;
  background: var(--brand-tint);
  color: #4338ca;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
.consent-box {
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  flex-shrink: 0;
}
.consent-box--minor {
  background: #fefce8;
  border: 1px solid #fde047;
}
.consent-box--minor .consent-text {
  color: #854d0e;
}
.consent-box--edu {
  background: #fff9f0;
  border: 1px solid #f3e2cf;
}
.consent-box--edu .consent-text {
  color: var(--warning-text);
}
.consent-text {
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.45;
}
.feedback {
  padding: 0.4rem 0.75rem;
  border-radius: 7px;
  font-size: 0.78rem;
  font-weight: 500;
  flex-shrink: 0;
}
.feedback--error {
  background: var(--danger-bg);
  color: var(--danger-text);
  border-left: 3px solid var(--danger);
}
.feedback--success {
  background: var(--success-bg);
  color: var(--success-text);
  border-left: 3px solid var(--success);
}
.mb-2 {
  margin-bottom: 0.4rem;
}
.btn-add {
  width: 11.5rem;
  align-self: flex-end;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.55rem 1.1rem;
  border-radius: 9px;
  border: none;
  background-color: var(--brand);
  color: var(--text-on-brand);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  white-space: nowrap;
}

.btn-label,
.btn-icon,
.btn-add-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}

.btn-icon {
  width: 1rem;
  flex-shrink: 0;
}

.btn-add-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.btn-add:hover:not(:disabled) {
  background-color: var(--brand-hover);
}

.btn-add:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-add:focus,
.btn-add:focus-visible {
  outline: none;
  box-shadow: none;
}

.btn-add:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.limit-reached {
  padding: 0.75rem;
  background: #f0fdf4;
  border-radius: 10px;
  font-size: 0.82rem;
  color: var(--success-text);
  border: 1px solid #bbf7d0;
  text-align: center;
}
.limit-hint {
  font-size: 0.78rem;
  color: var(--success-text);
  margin: 0.35rem 0 0;
  opacity: 0.85;
}
.search-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.btn-search {
  flex-shrink: 0;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 8px;
  border: 1px solid var(--neutral-200);
  background: var(--surface-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: all 0.2s;
}
.btn-search:hover:not(:disabled) {
  background: var(--brand-light);
  color: var(--text-on-brand);
  border-color: var(--brand-light);
}
.btn-search:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 14px;
}

.search-results-wrap {
  flex: 1;
  overflow-y: auto;
  min-height: 60px;
  max-height: 120px;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  background: #fafbfc;
}
.search-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.4;
}
.search-notice svg {
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: var(--text-subtle);
}
.results-list {
  list-style: none;
  padding: 0.25rem;
  margin: 0;
}
.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.result-item:hover {
  background: var(--surface-3);
}
.result-item--selected {
  background: #f0eeff;
}
.result-item--blocked {
  cursor: not-allowed;
  opacity: 0.7;
}
.result-item--blocked:hover {
  background: transparent;
}
.result-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.result-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-dark);
}
.result-meta {
  font-size: 0.72rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.result-check {
  flex-shrink: 0;
  color: var(--brand-light);
}
.result-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  white-space: nowrap;
}
.result-badge--member {
  background: var(--success-bg);
  color: var(--success-text);
}
.result-badge--minor {
  background: var(--warning-bg);
  color: var(--warning-text);
  border: 1px solid var(--warning-bg);
}

.col-members {
  padding: 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: #fafbfc;
  overflow: hidden;
}
.members-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}
.members-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
}
.members-counter {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  background: var(--neutral-200);
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
}
.progress-bar-wrap {
  height: 5px;
  background: var(--neutral-200);
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
}
.progress-bar-fill {
  height: 100%;
  background: #d8d0ff;
  border-radius: 999px;
  transition: width 0.4s ease;
}
.batch-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  background: var(--warning-bg);
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  font-size: 0.72rem;
  color: #854d0e;
  line-height: 1.4;
  flex-shrink: 0;
}
.batch-warning svg {
  flex-shrink: 0;
  margin-top: 0.1rem;
}
.members-list-wrap {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--neutral-400) transparent;
}
.members-list-wrap::-webkit-scrollbar {
  width: 4px;
}
.members-list-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.members-list-wrap::-webkit-scrollbar-thumb {
  background: var(--neutral-400);
  border-radius: 999px;
}
.members-empty {
  font-size: 0.78rem;
  color: var(--text-subtle);
  text-align: center;
  margin-top: 1.25rem;
  font-style: italic;
}
.members-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.member-item {
  border-radius: 9px;
  padding: 0.45rem 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.member-item--ok {
  background: #f0fdf4;
  border: 1px solid #86efac;
}
.member-item--error {
  background: var(--danger-bg);
  border: 1px solid var(--danger);
}
.member-item--clickable {
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}
.member-item--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(248, 113, 113, 0.12);
  border-color: #f87171;
}
.member-item--editing {
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.22);
  border-color: #818cf8;
}
.member-item-top {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}
.member-avatar {
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 999px;
  background-color: var(--success-bg);
  color: var(--success-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 1.55rem;
  margin-top: 0.05rem;
}

.member-avatar--error {
  background-color: var(--danger-bg);
  color: var(--danger-text);
}

.member-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.member-avatar-icon {
  font-size: 0.72rem;
  line-height: 1;
}
.member-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.member-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.member-email {
  font-size: 0.72rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btn-remove-item {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-subtle);
  padding: 0.15rem;
  border-radius: 4px;
  transition: color 0.2s;
  line-height: 1;
}
.btn-remove-item:hover {
  color: var(--danger);
}
.member-relationship-row {
  display: flex;
  justify-content: flex-end;
  gap: 0.3rem;
  margin-top: 0.2rem;
}
.member-group-name {
  color: #0f766e;
  background-color: #ccfbf1;
  max-width: 9rem;
  overflow: hidden;
  text-overflow: ellipsis;
}
.member-relationship-type {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--brand);
  background-color: var(--brand-tint);
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  white-space: nowrap;
}
.member-error-detail {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.3rem 0.4rem 0.1rem;
  border-top: 1px dashed var(--danger);
}
.error-edit-hint {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--danger-text);
}
.error-field {
  display: flex;
  gap: 0.3rem;
  font-size: 0.72rem;
}
.error-field-label {
  color: var(--danger-text);
  font-weight: 600;
  flex-shrink: 0;
}
.error-field-value {
  color: #7f1d1d;
}
.error-reason {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--danger-hover);
  margin-top: 0.15rem;
}
.members-footer {
  flex-shrink: 0;
  padding-top: 0.5rem;
  border-top: 1px solid var(--neutral-200);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.footer-actions {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  gap: 0.5rem;
  width: 100%;
}

.footer-actions > button {
  flex: 1 1 0;
  min-width: 0;
}
.retry-badge {
  align-self: center;
  background: var(--danger-bg);
  color: var(--danger-text);
  border: 1px solid var(--danger);
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
}
.btn-conclude {
  width: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.55rem 1.1rem;
  border-radius: 9px;
  border: none;
  background-color: var(--brand);
  color: var(--text-on-brand);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.btn-conclude:hover:not(:disabled) {
  background-color: var(--brand-hover);
}

.btn-conclude:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-conclude:focus,
.btn-conclude:focus-visible {
  outline: none;
  box-shadow: none;
}

.btn-conclude:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-skip-fixed {
  width: auto;
  padding: 0.55rem 1.1rem;
  border-radius: 9px;
  border: 1px solid var(--neutral-400);
  background-color: var(--neutral-200);
  color: var(--text-dark);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.btn-skip-fixed:hover:not(:disabled) {
  background-color: var(--neutral-300);
}

.btn-skip-fixed:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-skip-fixed:focus,
.btn-skip-fixed:focus-visible {
  outline: none;
  box-shadow: none;
}

.btn-skip-fixed:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
/* ── Avatar nos resultados de usuário existente ─────────────────────────── */
.result-name-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.result-avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 24px;
}

.result-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.result-avatar-icon {
  font-size: 0.72rem;
  line-height: 1;
}

.result-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
